import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  AlertCircle, Upload, FileText, X, Loader2, Send, Check, ShieldCheck, User, Globe,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Logo, Button, EmploymentHistory, DocumentViewer } from '../components';
import { inputCls, labelCls, cardCls } from './publicFormStyles';

/**
 * Pre-interview form — PUBLIC page at /pre-interview/:token
 *
 * "Before your interview": the first and only thing a candidate is sent before
 * an interview is booked. It asks for what an interviewer needs in front of
 * them — who they are, their CV, how they will evidence their Right to Work,
 * proof of NI, an existing DBS if they hold one, and their employment history.
 *
 * It asks for NO bank details and NO next of kin. Those only matter once
 * somebody has been offered the job and are on the personal details form
 * (/onboarding/:token), sent after a successful interview. A candidate should
 * not have to hand over their account number to attend an interview.
 *
 * Everything here is a declaration, not a check. The page never says a document
 * has been accepted or a right to work confirmed — the manager decides that,
 * and the copy says so, because a form that appears to approve itself sets an
 * expectation nobody has agreed to.
 *
 * No auth header is sent — the token in the URL IS the credential, so this uses
 * plain fetch rather than the authenticated axios instance.
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const sectionTitleCls = 'text-[15px] font-bold text-[#1E293B] dark:text-gray-100';

/**
 * Stricter than the server, which accepts 10MB. A phone photo of a passport is
 * comfortably under 5MB, and a candidate on mobile data is better told before
 * the upload than after it.
 */
const MAX_FILE_MB = 5;

const ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.heic';

/**
 * Split a stored single name into the two boxes the form shows.
 *
 * The record holds one `name`, because that is what every other screen in
 * ServePatch displays, but asking a person for "your name" in one box and then
 * splitting on whitespace guesses wrong for anyone with two surnames. So the
 * form asks for both halves and joins them back on save — the last word is the
 * surname only when we have never been told otherwise.
 */
const splitName = (full) => {
  const parts = String(full || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] };
};

const joinName = (first, last) => [first, last].map((part) => String(part || '').trim()).filter(Boolean).join(' ');

const PreInterviewForm = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fatal, setFatal] = useState('');
  const [form, setForm] = useState(null);

  // Which document group is mid-upload, so only that one shows a spinner.
  const [uploading, setUploading] = useState('');
  // The attached file being looked at, if any.
  const [preview, setPreview] = useState(null);
  const [missing, setMissing] = useState([]);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [provision, setProvision] = useState({ rtw_provision: '', rtw_share_code: '', dbs_availability: '' });
  const [history, setHistory] = useState([]);

  // Keeps the file inputs addressable per group without a ref for each one.
  const fileInputs = useRef({});

  /** Copy a server response into every piece of local state. */
  const adopt = useCallback((data) => {
    setForm(data);
    const { first, last } = splitName(data.candidate?.name);
    setFirstName(first);
    setLastName(last);
    setEmail(data.candidate?.email || '');
    setPhone(data.candidate?.phone || '');
    setProvision({
      rtw_provision: data.provision?.rtw_provision || '',
      rtw_share_code: data.provision?.rtw_share_code || '',
      dbs_availability: data.provision?.dbs_availability || '',
    });
    setHistory(Array.isArray(data.answers?.previous_employers) ? data.answers.previous_employers : []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_URL}/recruitment/pre-interview/${token}`);
        const body = await response.json();
        if (cancelled) return;
        if (!response.ok || !body.success) {
          setFatal(body.message || 'This link could not be opened.');
          return;
        }
        adopt(body.data);
      } catch {
        if (!cancelled) setFatal('We could not reach the server. Please check your connection and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, adopt]);

  const locked = Boolean(form?.locked);

  /** Save a draft, or send. Returns true when the save went through. */
  const save = async (submit) => {
    setSaving(true);
    setMissing([]);
    try {
      const response = await fetch(`${API_URL}/recruitment/pre-interview/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          candidate: { name: joinName(firstName, lastName), email, phone },
          provision,
          answers: { previous_employers: history },
          submit,
        }),
      });
      const body = await response.json();

      if (!response.ok || !body.success) {
        if (Array.isArray(body.missing_fields)) setMissing(body.missing_fields);
        toast.error(body.message || 'Could not save your answers.');
        return false;
      }

      adopt(body.data);
      toast.success(body.message);
      return true;
    } catch {
      toast.error('We could not reach the server. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  /** Attach one file to a document group. */
  const upload = async (groupId, file, docType) => {
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`That file is larger than ${MAX_FILE_MB}MB. Please attach a smaller one.`);
      return;
    }

    setUploading(groupId);
    try {
      const payload = new FormData();
      payload.append('file', file);
      if (docType) payload.append('doc_type', docType);

      const response = await fetch(`${API_URL}/recruitment/pre-interview/${token}/documents/${groupId}`, {
        method: 'POST',
        body: payload,
      });
      const body = await response.json();

      if (!response.ok || !body.success) {
        toast.error(body.message || 'Could not attach that file.');
        return;
      }
      adopt(body.data);
      toast.success(body.message);
    } catch {
      toast.error('We could not reach the server. Please try again.');
    } finally {
      setUploading('');
    }
  };

  const removeFile = async (storageId) => {
    setUploading(storageId);
    try {
      const response = await fetch(`${API_URL}/recruitment/pre-interview/${token}/documents/${storageId}`, {
        method: 'DELETE',
      });
      const body = await response.json();
      if (!response.ok || !body.success) {
        toast.error(body.message || 'Could not remove that file.');
        return;
      }
      adopt(body.data);
    } catch {
      toast.error('We could not reach the server. Please try again.');
    } finally {
      setUploading('');
    }
  };

  const setFileType = async (storageId, docType) => {
    try {
      const response = await fetch(`${API_URL}/recruitment/pre-interview/${token}/documents/${storageId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ doc_type: docType }),
      });
      const body = await response.json();
      if (!response.ok || !body.success) {
        toast.error(body.message || 'Could not save the document type.');
        return;
      }
      adopt(body.data);
    } catch {
      toast.error('We could not reach the server. Please try again.');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F8FC] dark:bg-[#1c2027]">
        <Loader2 size={30} className="animate-spin text-blue-600" />
      </div>
    );
  }

  if (fatal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F8FC] dark:bg-[#1c2027] px-4">
        <div className={`${cardCls} max-w-md text-center`}>
          <AlertCircle size={34} className="mx-auto text-amber-500 mb-3" />
          <h1 className="text-[17px] font-bold text-[#1E293B] dark:text-gray-100 mb-2">This link can&apos;t be opened</h1>
          <p className="text-[14px] text-[#64748B] dark:text-gray-400">{fatal}</p>
        </div>
      </div>
    );
  }

  const options = form.options || {};
  const documents = form.documents || [];
  // Only ask for a share code when they said they have one — an always-visible
  // box implies everybody needs one, and most candidates do not.
  const wantsShareCode = ['share_code', 'both'].includes(provision.rtw_provision);

  /** One attached file: its name, its type (where the group has types), remove. */
  // Opened in place. On a phone, where most of these forms are filled in, a new
  // tab is a separate screen and getting back to a half-finished form is a
  // gamble nobody should have to take to check they attached the right page.
  const fileRow = (group, file) => (
    <div key={file.id} className="rounded-xl border border-dashed border-[#C8D8EF] dark:border-gray-700 p-4 mb-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[14px] font-semibold text-[#1E293B] dark:text-gray-100">
            <FileText size={15} className="text-blue-600 shrink-0" />
            <span className="truncate">{file.file_name || 'Attached file'}</span>
          </div>
          {file.file_url && (
            <button
              type="button"
              onClick={() => setPreview({ url: file.file_url, name: file.file_name, title: group.label })}
              className="text-[12.5px] text-blue-600 hover:underline mt-1 inline-block"
            >
              Open
            </button>
          )}
        </div>
        {!locked && (
          <button
            type="button"
            onClick={() => removeFile(file.id)}
            disabled={uploading === file.id}
            className="text-[#94A3B8] hover:text-red-500 transition-colors shrink-0"
            aria-label={`Remove ${file.file_name || 'file'}`}
          >
            {uploading === file.id ? <Loader2 size={16} className="animate-spin" /> : <X size={16} />}
          </button>
        )}
      </div>

      {group.typed && (
        <div className="mt-3">
          <label className={labelCls} htmlFor={`type-${file.id}`}>Document type</label>
          <select
            id={`type-${file.id}`}
            className={inputCls}
            value={file.doc_type || ''}
            disabled={locked}
            onChange={(event) => setFileType(file.id, event.target.value)}
          >
            <option value="">Choose the type of document</option>
            {(options.rtwDocumentTypes || []).map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );

  /** One document group: what has been attached, and how to attach more. */
  const documentGroup = (group) => {
    const busy = uploading === group.id;
    return (
      <div key={group.id} className="mb-5 last:mb-0">
        {/* wrap, not truncate: a long document name and its status chip both
            matter, and on a phone the chip simply drops to the next line. */}
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-2">
          <h3 className={sectionTitleCls}>{group.label}</h3>
          {!group.uploaded && (
            <span className="text-[12px] font-semibold text-amber-700 bg-amber-50 dark:bg-amber-500/10 rounded-md px-2 py-0.5 shrink-0">
              Not attached
            </span>
          )}
        </div>
        <p className="text-[13px] text-[#64748B] dark:text-gray-400 mb-3">{group.hint}</p>

        {group.files.map((file) => fileRow(group, file))}

        {!locked && (
          <>
            <input
              type="file"
              accept={ACCEPT}
              className="hidden"
              ref={(element) => { fileInputs.current[group.id] = element; }}
              onChange={(event) => {
                const [file] = event.target.files || [];
                upload(group.id, file);
                // Reset, so choosing the same file twice still fires onChange.
                event.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => fileInputs.current[group.id]?.click()}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#CBD9EB] dark:border-gray-700 text-[13.5px] font-semibold text-[#0B3F8F] dark:text-blue-300 hover:bg-[#F1F6FD] dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
              {group.multiple
                ? (group.files.length ? `Add another ${group.label} document` : `Attach ${group.label}`)
                : (group.uploaded ? 'Replace file' : 'Choose file')}
            </button>
            <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-2">
              PDF, Word or image · up to {MAX_FILE_MB}MB
            </p>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F6F8FC] dark:bg-[#1c2027] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 flex justify-center"><Logo /></div>

        {/* Heading */}
        <div className={`${cardCls} mb-5`}>
          <h1 className="text-[22px] font-bold text-[#1E293B] dark:text-gray-100">Before your interview</h1>
          <p className="text-[13.5px] text-[#64748B] dark:text-gray-400 mt-1">
            {form.candidate?.name}
            {form.candidate?.role_applied_for ? ` · ${form.candidate.role_applied_for}` : ''}
          </p>
          <p className="text-[14px] text-[#475569] dark:text-gray-300 mt-3">
            Send your CV, Right to Work information and proof of National Insurance. Add an
            existing DBS certificate only if you already have one.
          </p>
          <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-2">
            This form never asks for your bank details. Those are only needed if you are offered
            the job.
          </p>

          {locked && (
            <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 p-3.5">
              <Check size={16} className="text-emerald-600 mt-0.5 shrink-0" />
              <p className="text-[13.5px] text-emerald-800 dark:text-emerald-300">
                You have already sent this form, so it can no longer be changed. Contact the
                recruiting manager if something needs correcting.
              </p>
            </div>
          )}
        </div>

        {/* Who they are */}
        <div className={`${cardCls} mb-5`}>
          <h2 className={`${sectionTitleCls} mb-4`}>Your details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls} htmlFor="first-name">First name *</label>
              <input
                id="first-name"
                className={inputCls}
                value={firstName}
                disabled={locked}
                onChange={(event) => setFirstName(event.target.value)}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="last-name">Surname *</label>
              <input
                id="last-name"
                className={inputCls}
                value={lastName}
                disabled={locked}
                onChange={(event) => setLastName(event.target.value)}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="email">Email *</label>
              <input
                id="email"
                type="email"
                className={inputCls}
                value={email}
                disabled={locked}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="phone">Telephone *</label>
              <input
                id="phone"
                className={inputCls}
                value={phone}
                disabled={locked}
                onChange={(event) => setPhone(event.target.value)}
              />
            </div>
          </div>
        </div>

        {/* CV */}
        <div className={`${cardCls} mb-5`}>
          {documents.filter((group) => group.id === 'cv').map(documentGroup)}
        </div>

        {/* Right to Work */}
        <div className={`${cardCls} mb-5`}>
          <h2 className={`${sectionTitleCls} mb-1`}>Right to Work</h2>
          <p className="text-[13px] text-[#64748B] dark:text-gray-400 mb-4">
            Your manager checks whether what you provide is sufficient. Nothing here confirms your
            right to work on its own.
          </p>

          {/* Which documents apply to whom, before the question that assumes you
              know. A British citizen sends a passport or a birth certificate
              with proof of NI and never needs a share code; everybody else uses
              a share code or immigration documents, and a birth certificate
              proves nothing. Somebody who does not know that picks "Nothing
              available yet" and the form comes back empty.

              Guidance, not a question — nothing here is stored or selected. */}
          {(options.rtwRoutes || []).length > 0 && (
            <div className="space-y-2.5 mb-5">
              {options.rtwRoutes.map((route) => {
                const Icon = route.icon === 'globe' ? Globe : User;
                return (
                  <div
                    key={route.id}
                    className="flex items-start gap-3 p-3.5 rounded-xl border border-[#E3EBF6] dark:border-gray-700 bg-[#F8FAFF] dark:bg-[#262b33]"
                  >
                    <div className="w-9 h-9 rounded-lg bg-white dark:bg-[#31373f] border border-[#E3EBF6] dark:border-gray-700 flex items-center justify-center shrink-0">
                      <Icon size={16} className="text-[#4B6A99] dark:text-blue-300" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13.5px] font-semibold text-[#1E293B] dark:text-gray-100">{route.title}</div>
                      <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-0.5">{route.hint}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mb-4">
            <label className={labelCls} htmlFor="rtw-provision">What can you provide?</label>
            <select
              id="rtw-provision"
              className={inputCls}
              value={provision.rtw_provision}
              disabled={locked}
              onChange={(event) => setProvision((current) => ({ ...current, rtw_provision: event.target.value }))}
            >
              <option value="">Choose one</option>
              {(options.rtwProvision || []).map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          {wantsShareCode && (
            <div className="mb-5">
              <label className={labelCls} htmlFor="share-code">Share code</label>
              <input
                id="share-code"
                className={inputCls}
                value={provision.rtw_share_code}
                disabled={locked}
                placeholder="e.g. W12 345 678"
                onChange={(event) => setProvision((current) => ({ ...current, rtw_share_code: event.target.value }))}
              />
            </div>
          )}

          {documents.filter((group) => group.id === 'rtw').map(documentGroup)}
        </div>

        {/* Proof of NI */}
        <div className={`${cardCls} mb-5`}>
          {documents.filter((group) => group.id === 'ni').map(documentGroup)}
        </div>

        {/* DBS */}
        <div className={`${cardCls} mb-5`}>
          <h2 className={`${sectionTitleCls} mb-1`}>DBS — if you already have one</h2>
          <p className="text-[13px] text-[#64748B] dark:text-gray-400 mb-4">
            You are not being asked to apply for a DBS check. Only tell us about one you already
            hold.
          </p>

          <div className="mb-4">
            <label className={labelCls} htmlFor="dbs-availability">Existing DBS certificate</label>
            <select
              id="dbs-availability"
              className={inputCls}
              value={provision.dbs_availability}
              disabled={locked}
              onChange={(event) => setProvision((current) => ({ ...current, dbs_availability: event.target.value }))}
            >
              <option value="">Choose one</option>
              {(options.dbsAvailability || []).map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          {documents.filter((group) => group.id === 'dbs').map(documentGroup)}
        </div>

        {/* Employment history */}
        <div className={`${cardCls} mb-5`}>
          <h2 className={`${sectionTitleCls} mb-1`}>Employment history</h2>
          <p className="text-[13px] text-[#64748B] dark:text-gray-400 mb-4">
            Add previous employment, including your current job if you have one. Explain any gaps.
            If this is your first job, leave this section empty.
          </p>
          <EmploymentHistory value={history} onChange={setHistory} readOnly={locked} />
        </div>

        {/* What is still missing, if the send was refused */}
        {missing.length > 0 && (
          <div className="mb-5 rounded-2xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-5">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle size={16} className="text-red-600" />
              <h3 className="text-[14px] font-bold text-red-800 dark:text-red-300">
                Still needed before you can send this
              </h3>
            </div>
            <ul className="list-disc list-inside text-[13.5px] text-red-700 dark:text-red-300 space-y-0.5">
              {missing.map((item) => <li key={item.field}>{item.label}</li>)}
            </ul>
          </div>
        )}

        {/* Actions */}
        {!locked && (
          <div className={`${cardCls} flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3`}>
            <div className="flex items-start gap-2 text-[12.5px] text-[#64748B] dark:text-gray-400">
              <ShieldCheck size={15} className="text-[#94A3B8] mt-0.5 shrink-0" />
              <span>You can save and come back to this link at any time before sending.</span>
            </div>
            {/* Stacked and full width on a phone: "Submit pre-interview form"
                beside "Save draft" on a 360px row leaves two buttons too narrow
                to read, let alone hit. */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 shrink-0">
              <Button variant="secondary" onClick={() => save(false)} disabled={saving} className="w-full sm:w-auto">
                Save draft
              </Button>
              <Button onClick={() => save(true)} loading={saving} leftIcon={<Send size={15} />} className="w-full sm:w-auto">
                Submit pre-interview form
              </Button>
            </div>
          </div>
        )}
      </div>

      <DocumentViewer file={preview} onClose={() => setPreview(null)} />
    </div>
  );
};

export default PreInterviewForm;
